import React from 'react'
import { Pill } from '../../../ui'
import { MemberPill } from './MemberPill.jsx'

/**
 * Assignee chip used by tasks. Thin wrapper around <MemberPill> that
 * resolves the display name from `(reportId, managerId, reportName,
 * managerName)` plus an optional snapshot-frozen `embeddedName`, then
 * delegates rendering. The orphan badge is rendered alongside via the
 * `trailing` prop.
 *
 * Props (unchanged from phase 2 plus the new `currentUserId` for the
 * "Me" replacement):
 *   assigneeId      — userId currently assigned
 *   reportId        — id of the report side of the pair
 *   managerId       — id of the manager side of the pair
 *   reportName      — display name for the report
 *   managerName     — display name for the manager
 *   currentUserId   — viewer; collapses the label to "Me" when matched
 *   isOrphaned      — show the orphan badge
 *   embeddedName    — for snapshot mode: assignee name frozen at session
 *                     completion time. Preferred over the live lookup so
 *                     historical records remain readable after a user is
 *                     renamed or deleted.
 *   onCycle         — click handler (omitted in snapshot/disabled mode)
 *   disabled        — interactions disabled (snapshot or locked sessions)
 */
export function AssigneeChip({
  assigneeId,
  reportId,
  managerId,
  reportName,
  managerName,
  currentUserId,
  isOrphaned = false,
  embeddedName = null,
  onCycle,
  disabled = false,
}) {
  let displayName
  if (embeddedName) {
    displayName = embeddedName
  } else if (assigneeId === reportId) {
    displayName = reportName || 'Report'
  } else if (assigneeId === managerId) {
    displayName = managerName || 'Manager'
  } else {
    displayName = 'Unknown'
  }

  const orphanBadge = isOrphaned ? (
    <Pill
      tone="danger"
      title="This task's previous assignee left. Reassign to clear."
      style={{ fontSize: 9, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: 0.3 }}
    >
      orphaned
    </Pill>
  ) : null

  return (
    <MemberPill
      userId={assigneeId}
      displayName={displayName}
      currentUserId={currentUserId}
      onClick={disabled ? undefined : onCycle}
      disabled={disabled}
      variant="task"
      ariaLabelPrefix="Assignee"
      trailing={orphanBadge}
    />
  )
}
